
import { supabase } from '@/integrations/supabase/client';
import { Patient } from '@/types';

/**
 * Service for patient-related operations
 */
export const patientService = {
  /**
   * Find a patient by email
   * @param email Patient email
   */
  findByEmail: async (email: string): Promise<{ patient: Patient | null, error: any }> => {
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('id, full_name, email, phone, address, gender, age, created_at')
        .eq('email', email);
      
      if (error) {
        console.error('Error checking for patient by email:', error);
        return { patient: null, error };
      }
      
      // Map the data to match the Patient type
      const mappedPatient = data && data.length > 0 ? {
        id: data[0].id,
        fullName: data[0].full_name,
        email: data[0].email || '',
        phone: data[0].phone,
        address: data[0].address || '',
        gender: data[0].gender || '',
        age: data[0].age || 0,
        createdAt: data[0].created_at
      } : null;
      
      return { patient: mappedPatient, error: null };
    } catch (error) {
      console.error('Error in findByEmail:', error);
      return { patient: null, error };
    }
  },
  
  /**
   * Find a patient by phone
   * @param phone Patient phone number
   */
  findByPhone: async (phone: string): Promise<{ patient: Patient | null, error: any }> => {
    if (!phone) return { patient: null, error: null };
    
    try {
      const { data, error } = await supabase
        .from('patients')
        .select('id, full_name, email, phone, address, gender, age, created_at')
        .eq('phone', phone);
      
      if (error) {
        console.error('Error checking for patient by phone:', error);
        return { patient: null, error };
      }
      
      // Map the data to match the Patient type
      const mappedPatient = data && data.length > 0 ? {
        id: data[0].id,
        fullName: data[0].full_name,
        email: data[0].email || '',
        phone: data[0].phone,
        address: data[0].address || '',
        gender: data[0].gender || '',
        age: data[0].age || 0,
        createdAt: data[0].created_at
      } : null;
      
      return { patient: mappedPatient, error: null };
    } catch (error) {
      console.error('Error in findByPhone:', error);
      return { patient: null, error };
    }
  },
  
  /**
   * Create a new patient
   * @param patientData Patient data
   */
  createPatient: async (patientData: {
    full_name: string,
    email: string | null,
    phone: string,
    address?: string | null,
    gender?: string | null,
    age?: number | null
  }): Promise<{ patientId: string | null, error: any }> => {
    try {
      const { data, error } = await supabase
        .from('patients')
        .insert(patientData)
        .select('id')
        .single();
      
      if (error) {
        console.error('Error creating new patient:', error);
        return { patientId: null, error };
      }
      
      if (!data || !data.id) {
        return { patientId: null, error: new Error('Failed to get ID of newly created patient') };
      }
      
      return { patientId: data.id, error: null };
    } catch (error) {
      console.error('Error in createPatient:', error);
      return { patientId: null, error };
    }
  },
  
  /**
   * Find existing patient or create a new one
   * @param patientData Patient data
   */
  findOrCreatePatient: async (patientData: {
    patientName: string,
    patientEmail: string,
    patientPhone: string,
    patientAddress?: string,
    patientGender?: string,
    patientAge?: number
  }): Promise<{ patientId: string | null, isExisting: boolean, error: any }> => {
    console.log('Checking if patient exists with:', patientData.patientEmail, patientData.patientPhone);
    
    // First try to find patient by email
    const { patient: emailPatient, error: emailError } = await patientService.findByEmail(patientData.patientEmail);
    
    if (emailPatient) {
      console.log('Found existing patient by email:', emailPatient);
      return { patientId: emailPatient.id, isExisting: true, error: null };
    }
    
    // If no match by email, try by phone
    const { patient: phonePatient, error: phoneError } = await patientService.findByPhone(patientData.patientPhone);
    
    if (phonePatient) {
      console.log('Found existing patient by phone:', phonePatient);
      return { patientId: phonePatient.id, isExisting: true, error: null };
    }
    
    // If patient doesn't exist, create a new one
    const formattedPatientData = {
      full_name: patientData.patientName,
      email: patientData.patientEmail,
      phone: patientData.patientPhone,
      address: patientData.patientAddress || null,
      gender: patientData.patientGender || null,
      age: patientData.patientAge || null
    };
    
    console.log('No existing patient found. Creating new patient:', formattedPatientData);
    
    const { patientId, error } = await patientService.createPatient(formattedPatientData);
    
    if (error) {
      return { patientId: null, isExisting: false, error };
    }
    
    console.log('Created new patient with ID:', patientId);
    return { patientId, isExisting: false, error: null };
  }
};
